import { useState, useEffect, useCallback } from "react";
import Marquee from "react-fast-marquee";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Header } from "../components/Header";
import { FilterBar } from "../components/FilterBar";
import { OpportunityCard } from "../components/OpportunityCard";
import { AccessGate } from "../components/AccessGate";

const Ribbon = ({ text }) => (
  <div className="bg-pink border-y-2 border-black py-2">
    <Marquee speed={55} gradient={false}>
      {Array(6).fill(0).map((_, i) => (
        <span key={i} className="font-display font-black text-black uppercase tracking-tight mx-6">
          {text} •
        </span>
      ))}
    </Marquee>
  </div>
);

export default function Directory() {
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setDiscipline] = useState("All");
  const [focus, setFocus] = useState("All");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("deadline");
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { sort };
    if (category !== "All") params.discipline = category;
    if (focus !== "All") params.focus = focus;
    if (search) params.search = search;
    const { data } = await api.get("/opportunities", { params });
    setOpps(data);
    setLoading(false);
  }, [category, focus, search, sort]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const s = settings || {};

  return (
    <AccessGate>
    <div className="App bg-white">
      <Header />

      {/* Hero */}
      <section className="border-b-2 border-black">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
          <p data-testid="hero-eyebrow" className="label-caps text-pink mb-5">{s.eyebrow}</p>
          <h1 className="font-display font-black uppercase tracking-tighter text-5xl sm:text-7xl leading-[0.9] max-w-4xl">
            {(s.headline || "").split("\n").map((line, i) => (
              <span key={i} className="block">{line}</span>
            ))}
            <span className="text-pink block">{s.headline_accent}</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-black/80">{s.intro}</p>
        </div>
      </section>

      <Ribbon text={s.marquee || ""} />

      {/* Body */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
        <div className="mb-8">
          <FilterBar {...{ discipline: category, setDiscipline, focus, setFocus, search, setSearch, sort, setSort }} />
        </div>

        {loading ? (
          <p data-testid="loading-state" className="label-caps text-black/50 py-20 text-center">Loading opportunities…</p>
        ) : opps.length === 0 ? (
          <div data-testid="empty-state" className="brutal-border p-12 text-center">
            <p className="font-display font-bold text-2xl">No matches found</p>
            <p className="text-black/60 mt-2">Try clearing filters or a different search.</p>
          </div>
        ) : (
          <>
            <p data-testid="results-count" className="label-caps text-black/50 mb-6">{opps.length} Opportunities</p>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {opps.map((o) => <OpportunityCard key={o.id} opp={o} />)}
            </div>
          </>
        )}

        {/* Submit CTA — under the listings */}
        <div className="mt-14 border-t-2 border-black pt-10 text-center">
          <h3 className="font-display font-black uppercase tracking-tighter text-2xl sm:text-3xl mb-3">{s.submit_heading || "Know of a Grant, Residency, Job or Open Call?"}</h3>
          <p className="text-black/70 mb-6 max-w-xl mx-auto">{s.submit_blurb || "Running an open call, grant, residency, or job for artists? Share it and we'll review it for the directory."}</p>
          <Link to="/submit" data-testid="submit-cta-btn"
            className="inline-flex items-center gap-2 label-caps bg-pink text-black px-8 py-3 border-2 border-black brutal-shadow-sm hover:-translate-y-0.5 transition-transform">
            {s.submit_button || "Submit an Opportunity"}
          </Link>
        </div>
      </section>

      <footer className="border-t-2 border-black mt-10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 flex flex-col sm:flex-row justify-between gap-4">
          <p className="font-display font-black tracking-tighter text-lg">RAW<span className="text-pink">FEMME</span>MEDIA</p>
          <p className="text-sm text-black/60">{s.footer_tagline}</p>
        </div>
      </footer>
    </div>
    </AccessGate>
  );
}
